/**
 * useEditableField - Hook for managing editable field state
 * Handles editing, saving, canceling, and restoring original values
 */

import { devLog, devError } from '../utils/devLog';
import { useState, useEffect, useCallback } from 'react';
import { useMultipleCharts } from '../components/common/AstrologyChart/MultipleChartsContext';
import { getStorageService } from '../services/StorageService';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { BaseEvent, ChartNavigationEventType, ChartNavigationEvent } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import { ChartFieldBackups } from '../types/ChartOverrides';

export interface UseEditableFieldOptions {
  /** Chart ID this field belongs to */
  chartId: string;
  /** Field key for storage (e.g., 'chart_name', 'place_name') */
  fieldKey: string;
  /** Initial value from API response */
  initialValue: string;
  /** Callback to get updated value path in apiResponse */
  getValuePath: (apiResponse: any) => string;
  /** Callback to set value in apiResponse (returns updated response) */
  setValueInResponse: (apiResponse: any, value: string) => any;
  /** Optional: Additional metadata update callback */
  updateMetadata?: (chartInstance: any, value: string) => any;
}

export interface UseEditableFieldReturn {
  /** Current display value */
  value: string;
  /** Whether field is in edit mode */
  isEditing: boolean;
  /** Current edit input value */
  editValue: string;
  /** Whether there's an original backup to restore */
  hasBackup: boolean;
  /** Original value (if backup exists) */
  originalValue: string | undefined;
  /** Start editing */
  startEdit: () => void;
  /** Update edit value */
  setEditValue: (value: string) => void;
  /** Save changes */
  save: () => Promise<void>;
  /** Cancel editing */
  cancel: () => void;
  /** Restore original value */
  restore: () => Promise<void>;
  /** Loading state */
  isLoading: boolean;
}

export function useEditableField(options: UseEditableFieldOptions): UseEditableFieldReturn {
  const { chartId, fieldKey, initialValue, getValuePath, setValueInResponse, updateMetadata } = options;

  const { charts, updateChart } = useMultipleCharts();

  // Read current value from context (source of truth)
  const getCurrentValue = useCallback(() => {
    if (!chartId) return initialValue;
    const chartInstance = charts.get(chartId);
    if (!chartInstance?.apiResponse) return initialValue;
    return getValuePath(chartInstance.apiResponse) || initialValue;
  }, [chartId, charts, getValuePath, initialValue]);

  // Initialize with value from context, not props
  const [value, setValue] = useState(() => getCurrentValue());
  const [editValue, setEditValue] = useState(() => getCurrentValue());
  const [isEditing, setIsEditing] = useState(false);
  const [backups, setBackups] = useState<ChartFieldBackups>({});
  const [isLoading, setIsLoading] = useState(false);

  const backupKey = `original_${fieldKey}` as keyof ChartFieldBackups;
  const hasBackup = !!backups[backupKey];

  // Load backups when chartId changes
  useEffect(() => {
    const loadBackups = async () => {
      if (chartId) {
        try {
          const storageService = getStorageService();
          const result = await storageService.getChartBackups(chartId);
          if (result.success && result.data) {
            setBackups(result.data.backups || {});
          } else {
            setBackups({});
          }
        } catch (error) {
          devError('Failed to load backups:', error);
          setBackups({});
        }
      }
    };
    loadBackups();
  }, [chartId]);

  // Sync value from context when chartId changes (but not on every render)
  useEffect(() => {
    if (!isEditing) {
      const currentValue = getCurrentValue();
      setValue(currentValue);
      setEditValue(currentValue);
    }
  }, [chartId, initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to chart data updates to get fresh values
  useEffect(() => {
    const subscription = globalEventStream.subscribe('chart.data.updated', (event) => {
      // Only update if this is our field and our chart
      if (event.metadata?.chartId === chartId && event.metadata?.type === fieldKey) {
        const newValue = event.metadata.value as string;
        setValue(newValue);
        if (!isEditing) {
          setEditValue(newValue);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [chartId, fieldKey, isEditing]);

  // Subscribe to chart activation - re-sync value from context when chart becomes active
  useEffect(() => {
    const subscription = globalEventStream.subscribe(
      ChartNavigationEventType.CHART_ACTIVATED,
      (event: ChartNavigationEvent) => {
        if (event.chartId === chartId && !isEditing) {
          // Re-read value from context when this chart is activated
          const currentValue = getCurrentValue();
          setValue(currentValue);
          setEditValue(currentValue);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [chartId, isEditing, getCurrentValue]);

  const startEdit = useCallback(() => {
    setEditValue(value);
    setIsEditing(true);
  }, [value]);

  const cancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
  }, [value]);

  const save = useCallback(async () => {
    if (!chartId) return;

    const chartInstance = charts.get(chartId);
    if (!chartInstance?.apiResponse) return;

    // Capture values before any state changes
    const currentValue = getValuePath(chartInstance.apiResponse);
    const newValue = editValue;

    // Update UI immediately - single batch of state updates
    // No intermediate loading state to prevent visual jerking
    setValue(newValue);
    setIsEditing(false);

    try {
      const storageService = getStorageService();

      // Create backup if first modification (async, doesn't affect UI)
      if (currentValue && !backups[backupKey]) {
        const newBackups = { ...backups, [backupKey]: currentValue };
        storageService.saveChartBackups(chartId, newBackups)
          .then(result => {
            if (result.success) {
              setBackups(newBackups);
            }
          })
          .catch(error => devError('Failed to save backup:', error));
      }

      // Update API response (metadata-only change, no full chart re-render needed)
      const updatedApiResponse = setValueInResponse(chartInstance.apiResponse, newValue);

      // Update metadata if callback provided
      let updatedMetadata = chartInstance.metadata;
      if (updateMetadata) {
        updatedMetadata = updateMetadata(chartInstance, newValue);
      }

      // Update in memory first - immediate, no await
      updateChart(chartId, {
        apiResponse: updatedApiResponse,
        metadata: updatedMetadata
      });

      // Save to storage in background (fire and forget for UI responsiveness)
      storageService.updateChartApiResponse(chartId, updatedApiResponse)
        .catch(error => devError(`Failed to save apiResponse for ${chartId}:`, error));

      if (updatedMetadata !== chartInstance.metadata) {
        storageService.updateChartMetadata(chartId, updatedMetadata)
          .catch(error => devError(`Failed to save metadata for ${chartId}:`, error));
      }

      // Emit event for storage-dependent components (RecentFilesList, LoadModal)
      devLog(`[useEditableField] EMIT chart.field.renamed: field=${fieldKey}, value="${newValue}", chartId=${chartId}`);
      globalEventStream.emit(createEvent<BaseEvent>(
        'chart.field.renamed',
        'editable-field',
        { metadata: { field: fieldKey, value: newValue, chartId: chartId } }
      ));
    } catch (error) {
      devError(`Error saving ${fieldKey}:`, error);
      // Revert on error
      setValue(currentValue);
    }
  }, [chartId, charts, updateChart, editValue, backups, backupKey, fieldKey, getValuePath, setValueInResponse, updateMetadata]);

  const restore = useCallback(async () => {
    if (!chartId || !backups[backupKey]) return;

    const chartInstance = charts.get(chartId);
    if (!chartInstance?.apiResponse) return;

    setIsLoading(true);
    try {
      const originalValue = backups[backupKey] || '';

      // Update API response (metadata-only change, no full chart re-render needed)
      const updatedApiResponse = setValueInResponse(chartInstance.apiResponse, originalValue);

      // Update in memory - only apiResponse, NOT chartData
      // This prevents unnecessary re-renders since chartData reference stays the same
      updateChart(chartId, {
        apiResponse: updatedApiResponse
      });

      // Save to storage
      const storageService = getStorageService();
      await storageService.updateChartApiResponse(chartId, updatedApiResponse);

      // Remove backup
      const newBackups = { ...backups };
      delete newBackups[backupKey];
      await storageService.saveChartBackups(chartId, newBackups);
      setBackups(newBackups);

      setValue(originalValue);
      setEditValue(originalValue);

      // Emit event for storage-dependent components
      devLog(`[useEditableField] EMIT chart.field.renamed (restore): field=${fieldKey}, value="${originalValue}", chartId=${chartId}`);
      globalEventStream.emit(createEvent<BaseEvent>(
        'chart.field.renamed',
        'editable-field',
        { metadata: { field: fieldKey, value: originalValue, chartId: chartId, restored: true } }
      ));
    } catch (error) {
      devError(`Failed to restore ${fieldKey}:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [chartId, charts, updateChart, backups, backupKey, fieldKey, setValueInResponse]);

  return {
    value,
    isEditing,
    editValue,
    hasBackup,
    originalValue: backups[backupKey],
    startEdit,
    setEditValue,
    save,
    cancel,
    restore,
    isLoading
  };
}
